/**
 * The web build's stand-in for the local index (PLAN §3.3 "Web build",
 * D12): the same API as `indexClient.js`, answered from the records the web
 * app already holds in memory (`records/web/scopeCache.js`, read through
 * `listRecords`), for the scopes the palette passes.
 *
 * - Titles, keys, paths, tags and locations only: no document body text on
 *   web this release (`bodyBackfilled` is always false, which the documents
 *   provider turns into "Text inside documents is searchable in the desktop
 *   app").
 * - Requests and collections are desktop-only panels: never returned.
 * - At most `ROW_CAP` rows per scope are searched; beyond that the answer
 *   says `partial: true` ("Showing results from loaded items").
 * - Rows are re-read when a record is written here (`onRecordsWritten`) and
 *   otherwise at most every `TTL_MS`, so a teammate's change shows up soon.
 */
import { abortError, indexTerms, normalizeText, TASK_KEY_RE } from './abort';
import { indexError } from './indexClient';

export const ROW_CAP = 50_000;
const TTL_MS = 3000;
const KINDS = ['task', 'board', 'document', 'folder', 'event', 'request', 'collection'];
const SOURCES = [
  { ext: 'task-manager', types: ['board', 'task'] },
  { ext: 'documents', types: ['document', 'folder'] },
  { ext: 'calendar', types: ['event'] },
];

function str(v) {
  return typeof v === 'string' ? v.trim() : '';
}

function taskKey(prefix, number) {
  const p = str(prefix).toUpperCase();
  return p && Number.isInteger(number) && number > 0 ? `${p}-${number}` : null;
}

/** Records of one scope → index rows (the same extraction as entity.rs). */
export function rowsFromRecords(scope, records) {
  const byId = new Map(records.map((r) => [r.id, r]));
  const boards = new Map();
  for (const r of records) if (r.record_type === 'board') boards.set(r.id, r);
  const pathOf = (parentId) => {
    const names = [];
    const seen = new Set();
    let root = null;
    let id = parentId;
    while (id && !seen.has(id) && names.length < 32) {
      seen.add(id);
      const p = byId.get(id);
      if (!p || (p.record_type !== 'folder' && p.record_type !== 'document')) break;
      names.push((p.record_type === 'folder' ? str(p.name) : str(p.title)) || '…');
      if (!p.parent_id) root = p.scopeId || null;
      id = p.parent_id;
    }
    return { path: names.reverse().join(' › '), root };
  };
  const out = [];
  for (const r of records) {
    const base = { kind: r.record_type, id: String(r.id), scope, parentId: null, title: '', detail: null, updatedAt: Number(r.updatedAt) || 0 };
    switch (r.record_type) {
      case 'board': {
        const prefixes = [str(r.keyPrefix).toUpperCase(), ...(Array.isArray(r.keyPrefixAliases) ? r.keyPrefixAliases : []).map((a) => str(a).toUpperCase())].filter(Boolean);
        out.push({ ...base, title: str(r.name), detail: [...new Set(prefixes)].join(' ') || null });
        break;
      }
      case 'task': {
        const boardId = r.scope_id || r.parent_id || null;
        const board = boards.get(boardId);
        const keys = [];
        if (board) {
          for (const p of [board.keyPrefix, ...(Array.isArray(board.keyPrefixAliases) ? board.keyPrefixAliases : [])]) {
            const k = taskKey(p, r.number);
            if (k && !keys.includes(k)) keys.push(k);
          }
        }
        out.push({ ...base, title: str(r.title), parentId: boardId, detail: keys.join(' ') || null, keyLabel: keys[0] || undefined });
        break;
      }
      case 'document':
      case 'folder': {
        const { path, root } = pathOf(r.parent_id);
        const tags = (Array.isArray(r.tags) ? r.tags : []).map(str).filter(Boolean);
        const detail = [path, tags.map((t) => `#${t}`).join(' ')].filter(Boolean).join(' · ');
        const rootScopeId = r.parent_id ? root : r.scopeId || null;
        out.push({
          ...base,
          title: r.record_type === 'folder' ? str(r.name) : str(r.title),
          parentId: r.parent_id || null,
          detail: detail || null,
          ...(rootScopeId ? { rootScopeId } : {}),
        });
        break;
      }
      case 'event': {
        if (r.override_of) break;
        const startAt = r.start || r.start_date || null;
        out.push({ ...base, title: str(r.title), detail: str(r.location) || null, ...(startAt ? { startAt } : {}) });
        break;
      }
      default:
        break;
    }
  }
  return out;
}

function hitOf(row, relevance) {
  const { _title, _hay, ...hit } = row;
  return { ...hit, relevance };
}

/**
 * @param {{ listRecords: (args: object) => Promise<object[]>, onRecordsWritten?: (fn: Function) => () => void,
 *           now?: () => number }} deps
 */
export function createWebIndexClient({ listRecords, onRecordsWritten, now = () => Date.now() } = {}) {
  const cache = new Map(); // scope -> { at, rows, partial }
  if (typeof onRecordsWritten === 'function') {
    onRecordsWritten(({ workspaceId } = {}) => {
      if (workspaceId) cache.delete(workspaceId);
      else cache.clear();
    });
  }

  async function load(scope) {
    const hit = cache.get(scope);
    if (hit && now() - hit.at < TTL_MS) return hit;
    const records = [];
    for (const { ext, types } of SOURCES) {
      for (const recordType of types) {
        // eslint-disable-next-line no-await-in-loop
        const list = await listRecords({ workspaceId: scope, extensionId: ext, recordType }).catch(() => []);
        for (const r of list || []) records.push(r);
      }
    }
    const partial = records.length > ROW_CAP;
    const rows = rowsFromRecords(scope, partial ? records.slice(0, ROW_CAP) : records).map((r) => ({
      ...r,
      _title: normalizeText(r.title),
      _hay: normalizeText(`${r.title} ${r.detail || ''}`),
    }));
    const entry = { at: now(), rows, partial };
    cache.set(scope, entry);
    return entry;
  }

  async function rowsFor(scopes) {
    const out = [];
    let partial = false;
    for (const s of scopes) {
      // eslint-disable-next-line no-await-in-loop
      const e = await load(s);
      partial = partial || e.partial;
      for (const r of e.rows) out.push(r);
    }
    return { rows: out, partial };
  }

  async function search(args = {}, { signal } = {}) {
    if (signal?.aborted) throw abortError();
    const kinds = args.kinds || [];
    const scopes = args.scopes || [];
    const limit = args.limit ?? 50;
    if (!kinds.length || kinds.some((k) => !KINDS.includes(k)) || scopes.length > 64) throw indexError('bad_args');
    if (!Number.isInteger(limit) || limit < 1 || limit > 200) throw indexError('bad_args');
    const q = String(args.q ?? '');
    const terms = indexTerms(q);
    if (q.length > 200 || terms.length > 10) throw indexError('query_too_long');
    const { rows, partial } = await rowsFor(scopes);
    if (signal?.aborted) throw abortError();
    const kindSet = new Set(kinds);
    const long = terms.filter((t) => [...t].length >= 3);
    const short = terms.filter((t) => [...t].length < 3);
    const key = TASK_KEY_RE.test(q.trim()) && terms.length === 1 ? q.trim().toUpperCase() : null;
    const scored = [];
    let mode = 'fts';
    if (terms.length) {
      if (!long.length) mode = 'prefix';
      for (const r of rows) {
        if (!kindSet.has(r.kind)) continue;
        if (key && r.kind === 'task' && String(r.detail || '').toUpperCase().split(' ').includes(key)) {
          scored.push({ r, rel: 1 });
          continue;
        }
        if (!long.length) {
          if (!r._title.startsWith(short[0]) || !short.slice(1).every((t) => r._title.includes(t))) continue;
          scored.push({ r, rel: r._title === short[0] ? 1 : 0.8 });
          continue;
        }
        if (!terms.every((t) => r._hay.includes(t))) continue;
        const inTitle = terms.filter((t) => r._title.includes(t)).length / terms.length;
        const atStart = r._title.startsWith(terms[0]) ? 0.2 : 0;
        scored.push({ r, rel: Math.min(key ? 0.99 : 1, 0.5 + inTitle * 0.3 + atStart) });
      }
    }
    scored.sort((a, b) => b.rel - a.rel || b.r.updatedAt - a.r.updatedAt || (a.r.id < b.r.id ? -1 : 1));
    return {
      hits: scored.slice(0, limit).map((s) => hitOf(s.r, s.rel)),
      mode,
      truncated: scored.length > limit,
      backfilled: true,
      bodyBackfilled: false,
      partial,
    };
  }

  async function get(args = {}, { signal } = {}) {
    if (signal?.aborted) throw abortError();
    const refs = args.refs || [];
    if (refs.length > 200 || (args.scopes || []).length > 64) throw indexError('bad_args');
    const { rows } = await rowsFor(args.scopes || []);
    if (signal?.aborted) throw abortError();
    const byRef = new Map(rows.map((r) => [`${r.kind}:${r.id}`, r]));
    return refs.map((ref) => byRef.get(`${ref.kind}:${ref.id}`)).filter(Boolean).map((r) => hitOf(r, 0));
  }

  return {
    search,
    get,
    status: async () => ({
      available: true,
      backfilled: true,
      bodyBackfilled: false,
      rows: {},
      bodyRows: 0,
      queueDepth: 0,
      schemaVersion: 0,
      lastRebuildAt: null,
    }),
    rebuild: async () => {
      cache.clear();
      return { started: true };
    },
    onProgress: () => () => {},
    onReady: () => () => {},
    invalidate: () => cache.clear(),
  };
}
